import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AppColorPalettePopover.css";
import { useTranslation } from "react-i18next";

interface AppColorPalettePopoverProps {
	anchorRef: React.RefObject<HTMLElement>;
	onSelect: (color: string) => void;
	onMoreColors: () => void;
	onClose: () => void;
	automaticLabel?: string;
}

export default function AppColorPalettePopover({
	anchorRef,
	onSelect,
	onMoreColors,
	onClose,
	automaticLabel,
}: AppColorPalettePopoverProps) {
    const { t } = useTranslation(["common"])
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
            });
        }
    }, [anchorRef]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(e.target as Node)
            ) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose, anchorRef]);

    if (!pos) return null;
    
    const THEME_BASE_COLORS = [
        "#FFFFFF", "#000000", "#E7E6E6", "#44546A",
        "#4472C4", "#ED7D31", "#A5A5A5", "#FFC000",
        "#5B9BD5", "#70AD47",
    ];

    const THEME_COLOR_COLUMNS: string[][] = THEME_BASE_COLORS.map((base) => [
        shadeColor(base, 0.6),
        shadeColor(base, 0.4),
        shadeColor(base, 0.2),
        base,
        shadeColor(base, -0.25),
        shadeColor(base, -0.5),
    ]);

    const STANDARD_COLORS = [
        "#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050",
        "#00B050", "#00B0F0", "#0070C0", "#002060", "#7030A0",
    ];

    function shadeColor (hex: string, percent: number): string {
        const f = parseInt(hex.slice(1), 16);
        const t = percent < 0 ? 0 : 255;
        const p = percent < 0 ? percent * -1 : percent;
        const R = f >> 16, G = (f >> 8) & 0x00ff, B = f & 0x0000ff;
        return (
            "#" +
            (
                0x1000000 +
                (Math.round((t - R) * p) + R) * 0x10000 +
                (Math.round((t - G) * p) + G) * 0x100 +
                (Math.round((t - B) * p) + B)
            )
                .toString(16)
                .slice(1)
        );
    }

	const swatchStyle = (color: string): React.CSSProperties => ({
		width: 18,
		height: 18,
		background: color,
		border: "1px solid rgba(0,0,0,0.15)",
		borderRadius: 2,
		cursor: "pointer",
		padding: 0,
	});

    return createPortal(
		<div
			ref={popoverRef}
			className="editor-color-popover"
			style={{ top: pos.top, left: pos.left }}
		>
            {automaticLabel &&
                <button
                    type="button"
                    className="automatic-button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onSelect("automatic"); onClose(); }}
                >
                    {automaticLabel}
                </button>
            }

			<div className="section-label">{t("common:htmlEditor.themeColors")}</div>
			<div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 2 }}>
				{THEME_COLOR_COLUMNS.map((column, colIdx) => (
					<div key={colIdx} className="d-flex flex-column gap-1">
						{column.map((color, rowIdx) => (
							<button
								key={rowIdx}
								type="button"
								title={color}
								style={swatchStyle(color)}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => { onSelect(color); onClose(); }}
							/>
						))}
					</div>
				))}
			</div>

			<div className="section-label">{t("common:htmlEditor.standardColors")}</div>
			<div className="d-flex gap-1">
				{STANDARD_COLORS.map((color) => (
					<button
						key={color}
						type="button"
						title={color}
						style={swatchStyle(color)}
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => { onSelect(color); onClose(); }}
					/>
				))}
			</div>

			<button
				type="button"
                className="more-colors-button"
				onMouseDown={(e) => e.preventDefault()}
				onClick={() => { onMoreColors(); onClose(); }}
			>
				{t("common:htmlEditor.moreColors")}
			</button>
		</div>,
		document.body
	);
}